import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Link } from 'gatsby'
import { Wrapper, H, P, Grid, Image } from '@farewill/ui'
import { COLOR } from '@farewill/ui/tokens'
import { screenMin, screenMax } from '@farewill/ui/helpers/responsive'
import { smallCaps } from '@farewill/ui/helpers/text'

import PATHS from 'paths'

import AspectRatioWrapper from 'components/AspectRatioWrapper'
import ChevronSeparator from 'components/ChevronSeparator'
import WrapperWithEdge from 'components/WrapperWithEdge'

import { BACKGROUND_TYPES } from '../constants'

const StyledTextureWrapper = styled(Wrapper)`
  z-index: 1;
  position: relative;
  overflow: hidden;
`

const StyledTextureLeft = styled(Image)`
  position: absolute;
  pointer-events: none;
  z-index: -1;
  transform: scaleX(-1);
  left: -15%;
  top: ${({ hasIntro }) => `${hasIntro ? '75%' : '40%'}`};

  ${screenMin.m`
    left: -10%;
    top: 30%;
  `}

  ${screenMin.l`
    left: -3%;
    top: 20%;
  `}

  ${screenMin.xl`
    left: -3%;
    top: ${({ hasIntro }) => `${hasIntro ? '20%' : '8%'}`};
  `}
`

const StyledTextureTop = styled(Image)`
  position: absolute;
  pointer-events: none;
  z-index: -1;
  transform: rotate(80deg);
  right: 15%;
  top: ${({ hasIntro }) => `${hasIntro ? '-20%' : '-40%'}`};

  ${screenMin.m`
    ${({ hasIntro }) => !hasIntro && 'top: -25%'};
  `}

  ${screenMin.l`
    right: 15%;
    top: ${({ hasIntro }) => `${hasIntro ? '-17%' : '-25%'}`};
  `}

  ${screenMin.xl`
    right: 15%;
    top: ${({ hasIntro }) => `${hasIntro ? '-20%' : '-25%'}`};
  `}
`

const StyledTextureRight = styled(Image)`
  position: absolute;
  pointer-events: none;
  z-index: -1;
  transform: rotate(215deg);

  bottom: -5%;
  right: -25%;

  ${screenMin.s`
    transform: rotate(205deg);
    bottom: ${({ hasIntro }) => `${hasIntro ? '-10%' : '-25%'}`};
    right: -5%;
  `}

  ${screenMin.m`
    transform: rotate(205deg);
    bottom: -10%;
    right: -5%;
  `}

  ${screenMin.l`
    transform: rotate(205deg);
    bottom: -10%;
    right: -2%;
  `}
`

const StyledBreadcrumb = styled(Link)`
  display: inline-block;
  text-decoration: none;
`

const StyledImageWrapper = styled(AspectRatioWrapper)`
  ${screenMax.s`
    display: none;
  `}
`

const StyledContentWrapper = styled(Grid.Item)`
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const StyledBreadcrumbWrapper = styled(Wrapper)`
  ${smallCaps}
  color: ${COLOR.BLACK};
`

const BackgroundSwitch = ({
  backgroundType,
  padding,
  paddingFromM,
  paddingFromXL,
  hasIntro,
  children,
}) => {
  switch (backgroundType) {
    case BACKGROUND_TYPES.TEXTURES:
      return (
        <StyledTextureWrapper
          background={COLOR.ACCENT.PRIMARY_60}
          padding={padding}
          paddingFromM={paddingFromM}
          paddingFromXL={paddingFromXL}
        >
          <StyledTextureTop
            path="textures/detail-1"
            width={125}
            widthFromL={150}
            widthFromXL={220}
            hasIntro={hasIntro}
            loading="auto"
          />
          <StyledTextureLeft
            path="textures/detail_new_yellow02"
            width={200}
            widthFromL={250}
            widthFromXL={350}
            hasIntro={hasIntro}
            loading="auto"
          />
          <StyledTextureRight
            path="textures/fingerprint_yellow02"
            width={200}
            widthFromL={300}
            widthFromXL={350}
            hasIntro={hasIntro}
            loading="auto"
          />
          {children}
        </StyledTextureWrapper>
      )
    case BACKGROUND_TYPES.EDGE:
      return (
        <WrapperWithEdge
          direction="down"
          edgeBackgroundColor={COLOR.WHITE}
          thickEdge
          padding={padding}
          paddingFromM={paddingFromM}
          paddingFromXL={paddingFromXL}
        >
          {children}
        </WrapperWithEdge>
      )

    case BACKGROUND_TYPES.PLAIN:
    default:
      return (
        <Wrapper
          padding={padding}
          paddingFromM={paddingFromM}
          paddingFromXL={paddingFromXL}
          background={COLOR.ACCENT.PRIMARY_60}
        >
          {children}
        </Wrapper>
      )
  }
}

const Header = ({ article, breadcrumbs }) => {
  const hasIntro = Boolean(article.intro)
  // We don't want to show an image if the Header has a textured or decorative background
  const hasImage =
    article.image &&
    (!article.backgroundType ||
      article.backgroundType === BACKGROUND_TYPES.PLAIN)

  return (
    <BackgroundSwitch
      backgroundType={article.backgroundType}
      padding={hasImage ? [0, 0, 'M'] : ['S', 0, 'XXL']}
      paddingFromM={hasImage ? [0, 0, 'M'] : ['XL', 0]}
      paddingFromXL={['XXL', 0]}
      hasIntro={hasIntro}
    >
      <Wrapper
        paddingFromM={hasImage ? 0 : ['XL', 0]}
        paddingFromXL={['XXL', 0]}
      >
        <Grid container gapFromM={['S', 'M']} gapFromL={['S', 'XL']}>
          {hasImage && (
            <StyledContentWrapper
              span={5}
              spanFromL={3}
              spanFromXL={3}
              startColumnFromM={2}
              startColumnFromL={3}
              startColumnFromXL={9}
            >
              {article?.image?.file && (
                <StyledImageWrapper
                  ratio={
                    article.image.file.details.image.width /
                    article.image.file.details.image.height
                  }
                >
                  <Image
                    path={article.image.file.url}
                    ext={['webp', 'png']}
                    formatPath={({ path, width, ext }) =>
                      `${path}?w=${width}&h=&fit=pad&fm=${ext}`
                    }
                    width={300}
                    widthFromL={300}
                    stretch
                  />
                </StyledImageWrapper>
              )}
            </StyledContentWrapper>
          )}
          <StyledContentWrapper
            spanFromM={10}
            spanFromL={8}
            spanFromXL={6}
            startColumnFromL={3}
            startColumnFromXL={hasImage ? 3 : 4}
            startColumnFromM={2}
          >
            <StyledBreadcrumbWrapper margin={[0, 0, 'XS']}>
              {breadcrumbs ? (
                <H context color={COLOR.BLACK} margin={[0, 0, 'XS']}>
                  <ChevronSeparator>
                    <StyledBreadcrumb to={PATHS.GENERAL.CONTENT_HUB}>
                      Guides
                    </StyledBreadcrumb>

                    <StyledBreadcrumb
                      to={`${PATHS.GENERAL.CONTENT_HUB}/${breadcrumbs.slug}`}
                    >
                      {breadcrumbs.shortTitle}
                    </StyledBreadcrumb>
                  </ChevronSeparator>
                </H>
              ) : (
                <StyledBreadcrumb to={PATHS.GENERAL.CONTENT_HUB}>
                  Guides
                </StyledBreadcrumb>
              )}
            </StyledBreadcrumbWrapper>

            <H tag="h1" size="L" decorative>
              {article.title}
            </H>
            {article?.intro?.intro && (
              <P color={COLOR.BLACK} size="L">
                {article.intro.intro}
              </P>
            )}
          </StyledContentWrapper>
        </Grid>
      </Wrapper>
    </BackgroundSwitch>
  )
}

Header.propTypes = {
  article: PropTypes.shape({
    title: PropTypes.string.isRequired,
    image: PropTypes.shape({
      file: PropTypes.shape({
        url: PropTypes.string.isRequired,
        details: PropTypes.shape({
          image: PropTypes.shape({
            width: PropTypes.number.isRequired,
            height: PropTypes.number.isRequired,
          }),
        }).isRequired,
      }).isRequired,
      title: PropTypes.string.isRequired,
    }),
    backgroundType: PropTypes.string,
    intro: PropTypes.shape({
      intro: PropTypes.string.isRequired,
    }),
  }).isRequired,
  guide: PropTypes.shape({
    title: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
  }).isRequired,
  breadcrumbs: PropTypes.shape({
    shortTitle: PropTypes.string,
    slug: PropTypes.string,
  }).isRequired,
}

BackgroundSwitch.propTypes = {
  backgroundType: PropTypes.string.isRequired,
  padding: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
  ]).isRequired,
  paddingFromM: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
  ]).isRequired,
  paddingFromXL: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
  ]).isRequired,
  hasIntro: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
}

export default Header
